import { useMemo, useState } from "react";
import {
  CalendarDays, CircleCheck, Circle, MapPin, Flag, ListChecks, Layers,
} from "lucide-react";

import { useResource } from "../lib/useApi";
import { date, relDays } from "../lib/fmt";
import { Sheet, Glass, Skeleton, Err, Empty, Pill } from "../ui";

/**
 * ProjectSheet — schedule, tasks, site visits and the laminate sheets.
 *
 * The site log and the laminate sheets are both endpoints that have been live
 * on the server for releases without a single caller. /portal/site-log is what
 * makes the "live-feed updates" promise on the sign-in screen literally true,
 * so it is a tab here rather than a row buried in Updates.
 */

const TABS = [
  ["schedule", "Schedule", Flag],
  ["tasks", "Tasks", ListChecks],
  ["visits", "Site log", MapPin],
  ["finishes", "Finishes", Layers],
];

export function ProjectSheet({ ctx, onClose }) {
  const { pid, project } = ctx;
  const [tab, setTab] = useState("schedule");

  return (
    <Sheet title={project?.title || "Your project"} onClose={onClose}>
      <p className="mono muted" style={{ marginTop: -6, fontSize: ".74rem" }}>
        {project?.project_code}
      </p>

      <div className="h-scroll" style={{ padding: "10px 0 14px" }}>
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            className={"rail-item" + (tab === key ? " on" : "")}
            onClick={() => setTab(key)}
          >
            <Icon size={18} strokeWidth={1.6} />
            {label}
          </button>
        ))}
      </div>

      {tab === "schedule" && <Schedule pid={pid} />}
      {tab === "tasks" && <Tasks pid={pid} />}
      {tab === "visits" && <Visits pid={pid} />}
      {tab === "finishes" && <Finishes pid={pid} />}
    </Sheet>
  );
}

/* ---- schedule ------------------------------------------------------------ */

function Schedule({ pid }) {
  const { data, loading, error, reload } = useResource(`/portal/project?project_id=${pid}`);
  if (loading) return <Stack n={4} />;
  if (error) return <Err onDismiss={reload}>{error}</Err>;

  const ms = data?.milestones || [];
  if (!ms.length) {
    return <Empty icon={Flag} title="No milestones yet" note="They appear as soon as your schedule is set." />;
  }

  return (
    <>
      <Glass className="stat" style={{ marginBottom: 14, padding: 16 }}>
        <b>{data.progress || 0}% complete</b>
        <div className="hero-track" style={{ margin: "8px 0 6px" }}>
          <i style={{ width: Math.min(100, Math.max(2, data.progress || 0)) + "%" }} />
        </div>
        <small>
          {data.task_done} of {data.task_total} tasks done
          {data.deadline ? ` · handover ${date(data.deadline)}` : ""}
        </small>
      </Glass>

      {ms.map((m) => {
        const pct = m.task_total > 0 ? Math.round((m.task_done / m.task_total) * 100) : 0;
        return (
          <Glass className="row" key={m.id}>
            <span className="row-ic" style={m.completed ? { background: "rgba(46,139,111,.14)", color: "var(--ok)" } : undefined}>
              {m.completed ? <CircleCheck size={18} /> : <Flag size={17} />}
            </span>
            <span className="row-txt">
              <b>{m.title}</b>
              <small>
                {m.completed
                  ? `Reached ${date(m.completed_at || m.due_date)}`
                  : `${m.task_done}/${m.task_total} tasks · ${pct}%`}
              </small>
            </span>
            <span className="row-end">
              {m.completed ? <Pill tone="ok">Reached</Pill> : (
                <>
                  <b style={{ fontSize: ".8rem" }}>{date(m.due_date)}</b>
                  <small>{relDays(m.due_date)}</small>
                </>
              )}
            </span>
          </Glass>
        );
      })}
    </>
  );
}

/* ---- tasks --------------------------------------------------------------- */

function Tasks({ pid }) {
  const { data, loading, error, reload } = useResource(`/portal/tasks?project_id=${pid}`);
  const [showDone, setShowDone] = useState(false);

  const groups = useMemo(() => {
    const out = new Map();
    for (const t of data || []) {
      const key = t.milestone_title || "Other work";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(t);
    }
    return [...out.entries()];
  }, [data]);

  if (loading) return <Stack n={5} />;
  if (error) return <Err onDismiss={reload}>{error}</Err>;
  if (!groups.length) return <Empty icon={ListChecks} title="No tasks yet" note="Your schedule is still being built." />;

  return (
    <>
      <button className="btn block" style={{ marginBottom: 14 }} onClick={() => setShowDone((v) => !v)}>
        {showDone ? "Hide completed" : "Show completed too"}
      </button>

      {groups.map(([name, tasks]) => {
        const shown = showDone ? tasks : tasks.filter((t) => !Number(t.done));
        if (!shown.length) return null;
        return (
          <section key={name} style={{ marginBottom: 18 }}>
            <p className="sec-title" style={{ padding: "0 0 8px" }}>{name}</p>
            {shown.map((t) => (
              <Glass className="row" key={t.id}>
                <span className="row-ic" style={Number(t.done) ? { background: "rgba(46,139,111,.14)", color: "var(--ok)" } : undefined}>
                  {Number(t.done) ? <CircleCheck size={17} /> : <Circle size={16} />}
                </span>
                <span className="row-txt">
                  <b style={Number(t.done) ? { color: "var(--ink-soft)" } : undefined}>{t.title}</b>
                  <small>{t.status_title || "Planned"}{t.deadline ? ` · ${date(t.deadline)}` : ""}</small>
                </span>
              </Glass>
            ))}
          </section>
        );
      })}
    </>
  );
}

/* ---- site log ------------------------------------------------------------ */

function Visits({ pid }) {
  const { data, loading, error, reload } = useResource(`/portal/site-log?project_id=${pid}`);
  if (loading) return <Stack n={4} />;
  if (error) return <Err onDismiss={reload}>{error}</Err>;
  if (!data?.length) {
    return <Empty icon={MapPin} title="No site visits logged yet" note="Every visit our team makes is recorded here, the day it happens." />;
  }
  return data.map((v) => (
    <Glass className="row" key={v.id}>
      <span className="row-ic"><MapPin size={17} /></span>
      <span className="row-txt">
        <b>{v.purpose || "Site visit"}</b>
        {v.notes && <small>{v.notes}</small>}
      </span>
      <span className="row-end">
        <b style={{ fontSize: ".8rem" }}>{date(v.visit_date)}</b>
      </span>
    </Glass>
  ));
}

/* ---- laminates ----------------------------------------------------------- */

function Finishes({ pid }) {
  const { data, loading, error, reload } = useResource(`/portal/laminates?project_id=${pid}`);
  if (loading) return <Stack n={2} />;
  if (error) return <Err onDismiss={reload}>{error}</Err>;
  if (!data?.length) {
    return <Empty icon={Layers} title="No finish sheet published yet" note="Your laminate and finish selections appear here once they are signed off." />;
  }
  return data.map((s) => (
    <Glass key={s.id} style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <b style={{ flex: 1, fontFamily: "var(--font-bold)" }}>{s.title || "Finish schedule"}</b>
        {s.revision ? <Pill>Rev {s.revision}</Pill> : null}
      </div>
      {(s.items || []).slice(0, 8).map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderTop: i ? "1px solid rgba(201,196,182,.4)" : 0 }}>
          <span style={{ flex: 1, fontSize: ".85rem", paddingLeft: Number(it.is_sub) ? 14 : 0 }}>{it.location}</span>
          <span className="mono" style={{ fontSize: ".76rem", color: "var(--mauve-deep)" }}>{it.code}</span>
          <span style={{ fontSize: ".76rem", color: "var(--ink-soft)" }}>{it.finish}</span>
        </div>
      ))}
      {(s.items || []).length > 8 && (
        <p className="muted" style={{ fontSize: ".76rem", margin: "8px 0 0" }}>
          +{s.items.length - 8} more rows on the printable sheet.
        </p>
      )}
      <a
        className="btn block"
        style={{ marginTop: 12 }}
        href={s.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open the printable sheet
      </a>
    </Glass>
  ));
}

function Stack({ n }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {Array.from({ length: n }, (_, i) => <Skeleton key={i} h={64} />)}
    </div>
  );
}
